import { useMe } from "@/api/services/admin/auth";
import { TProductContractDto } from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import { IconErrorOutline, IconRefresh } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SectionCard } from "@/components/elements/SectionCard";
import { SegmentedControl } from "@/components/elements/SegmentedControl/SegmentedControl";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { CONTRACT_FILTER_OPTIONS } from "@/features/organization/constants/organizationCreate";
import { FirstLoginModal } from "@/features/session/components/FirstLoginModal";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { black, state, text } from "@/theme/colors";
import { EProductCode } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { isNumber } from "@/utils/number";
import { truncateWithEllipsis } from "@/utils/string";
import { createUrlWithParams, openNewTab } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

type Props = {
  productContracts: TProductContractDto[];
  isLoading: boolean;
  onRefresh: () => void;
};

export const OrganizationCreateSetupProductRegistration: FC<Props> = ({ productContracts, isLoading, onRefresh }) => {
  const { id } = useParams();
  const [contractFilter, setContractFilter] = useState("");
  const { data: me } = useMe();

  // First-login setup modal: selecting a contract provisions the product admin account + hands off.
  // If the logged-in admin already has an account for that product (useMe.productAccounts), it's
  // already provisioned, so skip the "processing first login" modal and open the redirect right away.
  const [firstLoginContractId, setFirstLoginContractId] = useState<number | string | null>(null);

  const handleClickContractRow = (row: TProductContractDto) => {
    const productCode = row.product?.code;
    // Same tab name as the first-login flow below, so both paths reuse one tab per product.
    if (productCode && me?.data.productAccounts[productCode as EProductCode]) {
      openNewTab(
        createUrlWithParams(AppRoutes.mintOauthRedirect, { organizationId: id ?? row.organizationId, productCode }),
        `productContract:${row.id}`,
      );
      return;
    }

    setFirstLoginContractId(row.id);
  };

  const columns: MRT_ColumnDef<TProductContractDto>[] = useMemo(
    () => [
      {
        id: "product.code",
        header: "",
        Cell: ({ row }) =>
          row.original.product?.code ? (
            <ContractServiceBadge
              products={[{ code: row.original.product.code, singleCharLabel: row.original.product.singleCharLabel, name: row.original.product.name }]}
            />
          ) : (
            EMPTY_STR.TEXT
          ),
        size: 58,
      },
      {
        accessorKey: "product.name",
        header: "プロダクト名",
        Header: () => (
          <TableSorting name="productName">
            <Typography variant="body14Bold" color={text.primary}>
              プロダクト名
            </Typography>
          </TableSorting>
        ),
        size: 205,
        Cell: ({ row }) => <EllipsisText title={row.original.product?.name ?? EMPTY_STR.TEXT} variant="body14" maxWidth={180} />,
      },
      {
        id: "plan",
        header: "システム情報",
        size: 205,
        Cell: ({ row }) => {
          if (row.original.isTemporary) {
            return (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: state.error_3 }}>
                <IconErrorOutline fontSize="24px" />
                <Typography variant="body14" color={black[30]}>
                  仮登録
                </Typography>
              </Stack>
            );
          }
          return row.original.plan ? truncateWithEllipsis(row.original.plan, 18) : EMPTY_STR.TEXT;
        },
      },
      {
        id: "activeUserCount",
        header: "有効ユーザー数",
        size: 130,
        Cell: ({ row }) => (isNumber(row.original?.activeUserCount) ? row.original.activeUserCount : EMPTY_STR.TEXT),
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        Header: () => (
          <TableSorting name="lastLoginAt">
            <Typography variant="body14Bold" color={text.primary}>
              最終ログイン
            </Typography>
          </TableSorting>
        ),
        size: 148,
        Cell: () => EMPTY_STR.TEXT,
      },
      {
        id: "createdAt",
        header: "登録日",
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        size: 106,
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE),
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows: productContracts,
    filterRow: contractFilter ? (row) => row.status === contractFilter : undefined,
    onClickRow: handleClickContractRow,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.isTemporary ? `${state.warning_3} !important` : undefined,
      },
    }),
  });

  return (
    <>
      <SectionCard
        maxWidth="100%"
        title="各プロダクトの正式登録"
        titleVariant="head20Bold"
        titleColor={black[30]}
        headerAction={<SegmentedControl value={contractFilter} options={CONTRACT_FILTER_OPTIONS} onChange={setContractFilter} />}
      >
        <Stack gap={2}>
          <Table table={table} isLoading={isLoading} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body14" color={text.secondary} maxWidth="70%">
              それぞれのプロダクトを選択して専用の登録画面を開き、正式登録を進めてください。
              <br />
              仮登録の状態でもユーザー発行は可能ですが、お客様はプロダクトにアクセスできません。
            </Typography>
            <Stack
              flexDirection="row"
              alignItems="center"
              onClick={isLoading ? undefined : onRefresh}
              gap={1}
              sx={{
                cursor: isLoading ? "default" : "pointer",
                opacity: isLoading ? 0.5 : 1,
                pointerEvents: isLoading ? "none" : "auto",
                ":hover": {
                  opacity: isLoading ? 0.5 : 0.7,
                  transition: "all 0.2s ease",
                },
              }}
            >
              <Stack
                sx={{
                  display: "flex",
                  animation: isLoading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                <IconRefresh />
              </Stack>
              <Typography variant="body14" color={text.secondary}>
                登録状態を更新
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </SectionCard>

      <FirstLoginModal
        isOpen={firstLoginContractId != null}
        organizationId={id}
        productContractId={firstLoginContractId ?? undefined}
        onClose={() => setFirstLoginContractId(null)}
      />
    </>
  );
};
